// The two public legal documents, as typed content. LegalPage renders whichever it is given, so the
// page never needs to know which document it is showing, and the tests can check the shape and the
// facts (retention windows, the cookie, the sub-processors) against what the worker actually does.

export type LegalBlock = { kind: "paragraph"; text: string } | { kind: "bullets"; items: string[] };

export interface LegalSection {
  /** Anchor id, used for the contents list and deep links. */
  id: string;
  heading: string;
  blocks: LegalBlock[];
}

export interface LegalDocument {
  slug: "privacy" | "terms";
  eyebrow: string;
  title: string;
  lede: string;
  /** ISO date. */
  effectiveDate: string;
  sections: LegalSection[];
}

export type InlineSegment = { kind: "text"; text: string } | { kind: "link"; text: string; href: string };

export const LEGAL_EFFECTIVE_DATE = "2026-09-15";
export const SUPPORT_EMAIL = "support@loopgroup.ai";

const p = (text: string): LegalBlock => ({ kind: "paragraph", text });
const bullets = (...items: string[]): LegalBlock => ({ kind: "bullets", items });

const inlinePattern = new RegExp(String.raw`\[([^\]]+)\]\(([^)\s]+)\)|(${SUPPORT_EMAIL.replace(".", String.raw`\.`)})`, "gu");

/** Splits a paragraph into text and link segments: `[label](/path)` links and the bare support address. */
export function parseInline(text: string): InlineSegment[] {
  const segments: InlineSegment[] = [];
  let last = 0;
  for (const match of text.matchAll(inlinePattern)) {
    const index = match.index ?? 0;
    if (index > last) segments.push({ kind: "text", text: text.slice(last, index) });
    if (match[3]) segments.push({ kind: "link", text: match[3], href: `mailto:${match[3]}` });
    else segments.push({ kind: "link", text: match[1], href: match[2] });
    last = index + match[0].length;
  }
  if (last < text.length) segments.push({ kind: "text", text: text.slice(last) });
  return segments;
}

/** "2026-09-15" → "15 September 2026". */
export function formatEffectiveDate(iso: string): string {
  return new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" }).format(new Date(`${iso}T00:00:00Z`));
}

const privacy: LegalDocument = {
  slug: "privacy",
  eyebrow: "Privacy policy",
  title: "Privacy policy",
  lede: "What LoopGroup collects, why, and what it never touches. Written to be read.",
  effectiveDate: LEGAL_EFFECTIVE_DATE,
  sections: [
    {
      id: "who-we-are",
      heading: "Who we are",
      blocks: [
        p("LoopGroup shows you the recurring work your AI agents already run. We call each recurring job a loop. LoopGroup keeps a catalog of your loops, records how they perform when you choose to report runs, and lets you share a loop with a group or publish a card about it."),
        p(`LoopGroup is operated by the LoopGroup team. You can reach us at ${SUPPORT_EMAIL}. This policy covers loopgroup.ai, the LoopGroup API, and the LoopGroup MCP endpoint.`),
      ],
    },
    {
      id: "what-we-collect",
      heading: "What we collect",
      blocks: [
        p("We keep only what the product needs to show you your loops. This is the full list."),
        bullets(
          "Account: your display name, your email address, and the public half of each passkey you register (a credential id, a public key, a signature counter, and whether the passkey is backed up). Your biometric and the private key stay on your device. We never see them.",
          "Loops: name, description, cadence, schedule notes, tags, status, the evidence basis an agent gave for finding the loop, and how confident it was.",
          "Sources: the name of each agent environment that cataloged loops (for example “Codex · work laptop”), what it can see, and when it last synced.",
          "Run outcomes and telemetry, only for loops you chose to instrument: the run’s status, start and end time, duration, token counts, reported cost, a short outcome summary, and an error message when a run fails. Agents may attach small metrics of their own.",
          "Notes and acknowledgements that you or your agents leave on a loop.",
          "Definitions: the instructions a loop runs from, only when a person pastes them in or asks an agent to record them. The scan never collects them.",
          "Dependencies: the names of the tools, services, secrets, files, repositories, and models a loop needs. Names and references only. The server refuses anything that looks like a key or a token.",
          "Placements: which agent a loop runs on, when it moved, and the note left at the move.",
          "Groups, teams, memberships, and invitations you create, including the email address an invitation was addressed to.",
          "Public cards: the title, which loops are on the card, whether descriptions are included, and a view count.",
          "Agent keys: the name you gave the key, its scopes, the first few characters of the key, and a hash of it. We do not keep the key itself, so we cannot show it to you again.",
          "A session cookie when you sign in, described below.",
          "Request logs: the method, path, status code, and duration of each request to our servers. We use them to run the service and to spot abuse.",
          "A daily, hashed count of scan prompts created from each network address, so the public start page cannot be flooded. The count is deleted after three days.",
        ),
      ],
    },
    {
      id: "what-we-never-collect",
      heading: "What we never collect",
      blocks: [
        p("Some things do not belong in LoopGroup, and the product is built to keep them out."),
        bullets(
          "Secret values. Not API keys, not tokens, not passwords. The only key that reaches us is the LoopGroup key an agent uses to talk to LoopGroup.",
          "Prompt contents, unless you explicitly record them as a loop’s definition.",
          "Conversation text between you and your agent.",
          "Your customers’ data.",
          "Documents, files, or source code.",
          "The outputs your loops produce. A run carries a one-line summary at most.",
          "Your biometrics. Passkeys keep them on your device.",
        ),
        p("The scan prompt says this in writing to the agent, and the server checks dependency names for anything shaped like a token before saving them."),
      ],
    },
    {
      id: "how-agents-send-data",
      heading: "How agents send data",
      blocks: [
        p("LoopGroup starts with a prompt, not a form. The first prompt is scan-only. It authorizes one catalog sync: names, purpose, cadence, schedule hints, tags, confidence, an evidence basis, and dependencies by name. It does not edit any loop, install anything, or report a run."),
        p("Ongoing catalog sync and per-loop run reporting are separate choices. You approve each one after you claim your catalog, and the agent must show you its plan before changing anything in your environment."),
        p("Every agent key is scoped to read, report, or manage, and every key can be revoked in Settings. Revoking a key stops everything that key could send, immediately."),
      ],
    },
    {
      id: "temporary-catalogs",
      heading: "Temporary catalogs",
      blocks: [
        p("Creating a scan prompt on the start page creates a temporary private catalog with its own report-only key. The claim link stays in your browser; it is never part of the prompt."),
        p("If you do not claim the catalog within 14 days, a daily job deletes it: the loops, the sources, the sync history, and the key. Claiming it attaches everything to your account."),
      ],
    },
    {
      id: "public-cards",
      heading: "Public cards",
      blocks: [
        p("A public card is the one thing that leaves LoopGroup without a sign-in. It shows a fixed set of fields and nothing else:"),
        bullets(
          "The card title, your display name, and when the card was generated.",
          "For each loop: its name, the agent it runs on, its cadence, its evidence stage, its reliability with the number of resolved outcomes, and a coarse last-signal phrase such as “2 hours ago”.",
          "A seven-day rhythm of succeeded, failed, and open runs.",
          "Each loop’s description, only if you turned on “Include each loop’s purpose” when you created the card.",
        ),
        p("A card never shows run summaries, errors, tokens, cost, keys, email addresses, internal ids, or exact timestamps. The link is unguessable and is shown to you once. Revoking a card in Settings makes its page, image, and badge answer “not found” together."),
      ],
    },
    {
      id: "sharing-with-groups",
      heading: "Sharing with groups",
      blocks: [
        p("Sharing is per loop, per group, and reversible. When you share a loop into a group, its members can see the loop’s name, cadence, outcomes, reliability, notes, definitions, dependency names, and where it runs. They see your display name as the person who shared it."),
        p("Members of a group see each other’s display names and email addresses on the Members page. A group never sees your private catalog, your other sources, or loops you did not share. Removing a share takes the group’s access away and keeps the loop and its history in your workspace."),
      ],
    },
    {
      id: "where-your-data-lives",
      heading: "Where your data lives",
      blocks: [
        p("LoopGroup runs on Cloudflare Workers and stores data in Cloudflare D1. Your data is stored in the United States by default. Requests are served from Cloudflare’s network near you, so request logs may be handled briefly in other regions."),
      ],
    },
    {
      id: "how-long-we-keep-it",
      heading: "How long we keep it",
      blocks: [
        bullets(
          "Workspace data stays until you delete it or your account.",
          "Unclaimed temporary catalogs are deleted after 14 days.",
          "Sessions last 30 days or until you sign out. Expired sessions and sign-in challenges are cleared daily.",
          "Request logs are kept for a short period, measured in days.",
        ),
        p(`You can archive loops, revoke keys and cards, remove shares, and leave groups inside the product. To delete a loop’s history or your whole account, email ${SUPPORT_EMAIL}. We complete deletion within 30 days and confirm by reply. Self-serve deletion is on the roadmap, and this policy will change when it ships.`),
      ],
    },
    {
      id: "what-we-do-not-do",
      heading: "What we do not do",
      blocks: [
        bullets(
          "We do not sell your data.",
          "We do not show advertising.",
          "We do not use your data to train models.",
          "We do not send your data to Anthropic, OpenAI, xAI, or any other agent provider. Your agent calls LoopGroup; LoopGroup never calls your agent.",
          "We do not read your workspace except to run the service, investigate abuse, or help you when you ask.",
        ),
      ],
    },
    {
      id: "sub-processors",
      heading: "Sub-processors",
      blocks: [
        p("We use a small number of providers to run LoopGroup:"),
        bullets(
          "Cloudflare, Inc. — hosting, storage, network delivery, and request logs.",
          "Resend — transactional email about your account, when we need to send one. There is no marketing email.",
        ),
        p("We will update this list before adding a provider that handles your data."),
      ],
    },
    {
      id: "cookies-and-local-storage",
      heading: "Cookies and local storage",
      blocks: [
        p("LoopGroup sets one cookie, lg_session, when you sign in. It is HttpOnly, Secure, and SameSite, lasts 30 days, and is deleted when you sign out. There are no analytics cookies and no third-party cookies."),
        p("Your browser’s local storage remembers which workspace you had open. While you create a scan prompt, session storage holds the claim link until you claim or close the tab. Nothing in either is sent anywhere."),
      ],
    },
    {
      id: "security",
      heading: "Security",
      blocks: [
        bullets(
          "Sign-in uses passkeys. There are no passwords to leak.",
          "Agent keys, card links, claim links, and session tokens are stored as SHA-256 hashes. We keep a short prefix so you can recognise a key in the list.",
          "Every connection uses TLS.",
          "Account actions are checked against the site’s origin, and the public start page is rate-limited.",
        ),
        p(`No system is perfect. If you find a security problem, email ${SUPPORT_EMAIL} and we will respond quickly.`),
      ],
    },
    {
      id: "your-rights",
      heading: "Your rights",
      blocks: [
        p(`You can ask us to access, correct, delete, or export your data, or to restrict how we use it. Email ${SUPPORT_EMAIL}; we answer within 30 days and may ask you to confirm your identity with the passkey on your account.`),
        p("If you are in the European Economic Area or the United Kingdom, the GDPR gives you these rights, along with the right to object and to complain to your supervisory authority. We process your data to provide the service you asked for and, for logs and abuse prevention, in our legitimate interest in keeping it running."),
        p("If you are a California resident, the CCPA gives you the right to know, delete, and correct your personal information, and not to be treated differently for using those rights. We do not sell or share personal information as the CCPA defines those terms."),
      ],
    },
    {
      id: "children",
      heading: "Children",
      blocks: [p("LoopGroup is not for people under 16. We do not knowingly collect their data. If you believe we have, email us and we will delete it.")],
    },
    {
      id: "changes-to-this-policy",
      heading: "Changes to this policy",
      blocks: [
        p("When this policy changes, we update the effective date at the top. For a material change we will show a notice in the product or email account holders before it takes effect. Product changes are listed on the [changelog](/changelog)."),
      ],
    },
    {
      id: "contact",
      heading: "Contact",
      blocks: [p(`Questions, requests, and complaints: ${SUPPORT_EMAIL}.`)],
    },
  ],
};

const terms: LegalDocument = {
  slug: "terms",
  eyebrow: "Terms of service",
  title: "Terms of service",
  lede: "What you agree to when you use LoopGroup. Short on purpose.",
  effectiveDate: LEGAL_EFFECTIVE_DATE,
  sections: [
    {
      id: "the-service",
      heading: "The service",
      blocks: [
        p("LoopGroup catalogs the recurring work your AI agents run, records how those loops perform when you choose to report runs, and lets you share loops with a group or publish a card. It is in beta. Features will change, and some may be removed."),
        p("By creating an account, sending a scan prompt, or connecting an agent, you agree to these terms and to our [privacy policy](/privacy)."),
      ],
    },
    {
      id: "who-can-use-loopgroup",
      heading: "Who can use LoopGroup",
      blocks: [
        bullets(
          "You must be at least 16 years old.",
          "If you use LoopGroup for an organisation, you confirm that you may accept these terms for it.",
          "You may connect only agent environments that are yours or that you are authorised to connect.",
        ),
      ],
    },
    {
      id: "your-account-and-your-keys",
      heading: "Your account and your keys",
      blocks: [
        p("Your account is tied to a passkey. Keep the device that holds it safe."),
        p(`You are responsible for the agent keys you create, for where you store them, and for what your agents send with them. If a key is exposed, revoke it in Settings and create a new one. Tell us at ${SUPPORT_EMAIL} if you think your account was used by someone else.`),
      ],
    },
    {
      id: "acceptable-use",
      heading: "Acceptable use",
      blocks: [
        p("Use LoopGroup for what it is built for. Do not:"),
        bullets(
          "Try to access a workspace, loop, or card that was not shared with you, or probe for one.",
          "Scrape the service or send automated traffic outside the documented API and MCP endpoints.",
          "Send unlawful content, or content you have no right to send, including other people’s personal data or secrets.",
          "Interfere with the service, get around rate limits, or test its security without written permission.",
          "Use LoopGroup to harm, harass, or deceive anyone.",
        ),
      ],
    },
    {
      id: "your-data",
      heading: "Your data",
      blocks: [
        p("Your data stays yours. Loop names, descriptions, run history, notes, definitions, dependencies, and everything else you or your agents put into LoopGroup remain yours."),
        p("You give us a licence to store, process, display, back up, and transmit that data only as needed to run the service for you and for the people and agents you share it with. We do not train models on it and we do not sell it. The licence ends when the data is deleted."),
        p("You are responsible for having the right to send the data your agents send. The scan prompt asks the agent for operational metadata only; check what your agent reports if you are unsure."),
      ],
    },
    {
      id: "sharing-and-public-cards",
      heading: "Sharing and public cards",
      blocks: [
        p("Shares and public cards are published at your direction. You choose which loop goes to which group and which loops appear on a card. You are responsible for what you share and for the people you invite to your groups."),
        p("You can remove a share or revoke a card at any time. A revoked card stops answering immediately. Copies that other services cached, such as link previews, are outside our control."),
      ],
    },
    {
      id: "agents-act-on-your-instructions",
      heading: "Agents act on your instructions",
      blocks: [
        p("LoopGroup gives you prompts to send to your agent. The first prompt is scan-only and asks the agent to change nothing. Later prompts ask the agent to show you a plan before it edits anything, and to apply only what you approve."),
        p("The agent runs in your environment, under your account with that provider, and follows your instructions. You are responsible for reviewing what it does there. LoopGroup is not responsible for changes an agent makes to your systems."),
      ],
    },
    {
      id: "beta-and-no-warranty",
      heading: "Beta and no warranty",
      blocks: [
        p("LoopGroup is provided as is and as available. We do not promise that it will be uninterrupted or error-free, or that its numbers are complete. Reliability, cost, and rhythm are computed only from what your agents report, and the product says so beside each value."),
        p("Keep your own copies of anything you cannot afford to lose. We may change or stop the service, in whole or in part, with reasonable notice where we can give it."),
      ],
    },
    {
      id: "limitation-of-liability",
      heading: "Limitation of liability",
      blocks: [
        p("To the extent the law allows, we are not liable for indirect, incidental, or consequential losses, or for lost profits, revenue, or data, arising from your use of LoopGroup."),
        p("Our total liability to you for everything arising from these terms, in any twelve-month period, is limited to the greater of the amount you paid us in that period and 100 US dollars. During the beta, LoopGroup is free."),
        p("Nothing in these terms limits liability that the law says cannot be limited."),
      ],
    },
    {
      id: "suspension-and-termination",
      heading: "Suspension and termination",
      blocks: [
        p(`You can stop using LoopGroup at any time. Revoke your keys, and email ${SUPPORT_EMAIL} if you want your account and its data deleted.`),
        p("We may suspend or close an account that breaks these terms, puts other users at risk, or has been inactive for a long time. We will tell you why unless the law prevents it. When an account is closed, we delete its data as the privacy policy describes."),
      ],
    },
    {
      id: "changes-to-these-terms",
      heading: "Changes to these terms",
      blocks: [
        p("We may change these terms. When we do, we update the effective date at the top. For a material change we will show a notice in the product or email account holders before it applies. Using LoopGroup after that date means you accept the new terms."),
      ],
    },
    {
      // Governing law is a placeholder until counsel confirms the entity's jurisdiction. Texas was chosen
      // because nothing in the repository names another one; LegalPage carries the same note in its markup.
      id: "governing-law",
      heading: "Governing law",
      blocks: [
        p("These terms are governed by the laws of the State of Texas, United States, without regard to its conflict-of-law rules. Any dispute will be brought in the state or federal courts located in Texas, and you agree to their jurisdiction. If you are a consumer in a place whose law gives you extra protections, those protections still apply."),
      ],
    },
    {
      id: "contact",
      heading: "Contact",
      blocks: [p(`Questions about these terms: ${SUPPORT_EMAIL}.`)],
    },
  ],
};

export const legalDocuments: Record<LegalDocument["slug"], LegalDocument> = { privacy, terms };
